import { Component, ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { logger } from '../utils/logger'

interface Props {
  children: ReactNode
  /** Label shown in the error UI so users know which section crashed */
  groupName?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Lightweight error boundary for route groups.
 * Unlike the top-level ErrorBoundary, this renders inline (within the app layout)
 * so the sidebar / nav remain usable and other route groups keep working.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(
      `RouteErrorBoundary [${this.props.groupName ?? 'unknown'}] caught:`,
      error,
      errorInfo,
    )
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        routeGroup: this.props.groupName,
      },
    })

    // Auto-reload on ChunkLoadError (stale deployment chunks)
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch dynamically imported module')
    if (isChunkError) {
      const reloadKey = 'chunk_error_reload'
      const lastReload = sessionStorage.getItem(reloadKey)
      if (!lastReload) {
        sessionStorage.setItem(reloadKey, Date.now().toString())
        window.location.reload()
        return
      }
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            padding: '2rem',
          }}
        >
          <div
            style={{
              maxWidth: '500px',
              width: '100%',
              background: 'var(--color-surface, #1e1e2e)',
              borderRadius: 0,
              padding: '2rem',
              border: '1px solid var(--color-border, rgba(255,255,255,0.1))',
            }}
          >
            <h2
              style={{
                marginTop: 0,
                color: 'var(--color-danger, #C4554D)',
                fontSize: '1.25rem',
              }}
            >
              {this.props.groupName
                ? `${this.props.groupName} encountered an error`
                : 'This section encountered an error'}
            </h2>
            <p style={{ color: 'var(--color-text-secondary, #A2A8B0)', lineHeight: 1.6 }}>
              Something went wrong loading this page. The rest of the app should still work
              normally.
            </p>

            {this.state.error && !import.meta.env.PROD && (
              <details
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-background, #121218)',
                  borderRadius: 0,
                  fontSize: '0.84375rem',
                }}
              >
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Error Details</summary>
                <pre
                  style={{
                    marginTop: '0.5rem',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </pre>
              </details>
            )}

            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 0,
                  border: 'none',
                  backgroundColor: 'var(--color-primary, #C8A45C)',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  window.location.href = '/dashboard'
                }}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 0,
                  border: '1px solid var(--color-border, rgba(255,255,255,0.2))',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text, #E4E1DB)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
